import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MessageSquare,
  Search,
  Paperclip,
  HelpCircle,
  MoreVertical,
  Phone,
  Home,
  Loader2,
  AlertCircle,
  Send,
  Circle,
  Mail,
  MailOpen,
  Wrench,
  PenSquare,
  ChevronLeft,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import Layout from '../components/Layout';
import { getApiUrl } from '../config/api';
import authFetch, { parseToken } from '../utils/authFetch';
import useIsMobile from '../hooks/useIsMobile';
import './Messages.css';

// Get initials from name
const getInitials = (name) => {
  if (!name || name === 'Unknown') return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

// Format a phone number like +15551234567 into +1 (555) 123-4567
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

// Generate a consistent color based on the name
const getAvatarColor = (name) => {
  const colors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#F97316', // orange
  ];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Returns true if the browser already holds a usable session (a non-expired
// access token, or a refresh token authFetch can use to mint a new one).
const hasValidSession = () => {
  const payload = parseToken(localStorage.getItem('access_token'));
  if (payload && Date.now() < payload.exp * 1000) return true;
  const refreshPayload = parseToken(localStorage.getItem('refresh_token'));
  return Boolean(refreshPayload && Date.now() < refreshPayload.exp * 1000);
};

// Read the tenant_id out of an SMS deep-link token without verifying it.
// itsdangerous tokens are "base64url(json).timestamp.signature"; the payload
// is plain base64url-encoded JSON we can decode client-side.
const decodeDeepLinkTenantId = (token) => {
  try {
    let b64 = token.split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const payload = JSON.parse(atob(b64));
    return payload.tenant_id != null ? String(payload.tenant_id) : null;
  } catch {
    return null;
  }
};

// Parse UTC timestamp and convert to local Date object
// Handles timestamps with or without 'Z' suffix
const parseUTCTimestamp = (isoString) => {
  if (!isoString) return null;
  // If the timestamp doesn't end with Z or timezone offset, treat it as UTC
  let utcString = isoString;
  if (!isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}:\d{2}$/)) {
    utcString = isoString + 'Z';
  }
  return new Date(utcString);
};

// Format date for display (converts UTC to local timezone)
const formatTimestamp = (isoString) => {
  const date = parseUTCTimestamp(isoString);
  if (!date) return '';
  
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    // Today - show time in user's local timezone
    return date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  } else {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
};

// Format time for message bubbles (converts UTC to local timezone)
const formatMessageTime = (isoString) => {
  const date = parseUTCTimestamp(isoString);
  if (!date) return '';
  
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Format date separator for messages (iMessage-style day headers)
const formatDateSeparator = (isoString) => {
  const date = parseUTCTimestamp(isoString);
  if (!date) return '';
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffMs = today - messageDate;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    // Within last week - show day name and date (e.g., "Monday, Feb 3")
    return date.toLocaleDateString(undefined, { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    });
  } else if (date.getFullYear() === now.getFullYear()) {
    // Same year - show day, month, and date (e.g., "Monday, Feb 3")
    return date.toLocaleDateString(undefined, { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    });
  } else {
    // Different year - include year (e.g., "Monday, Feb 3, 2024")
    return date.toLocaleDateString(undefined, { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  }
};

// Check if two dates are on different calendar days (in local timezone)
const isDifferentDay = (isoString1, isoString2) => {
  const date1 = parseUTCTimestamp(isoString1);
  const date2 = parseUTCTimestamp(isoString2);
  
  if (!date1 || !date2) return true;
  
  return (
    date1.getFullYear() !== date2.getFullYear() ||
    date1.getMonth() !== date2.getMonth() ||
    date1.getDate() !== date2.getDate()
  );
};

// Conversation List Component
function ConversationList({ conversations, activeConversationId, onSelectConversation, onNewMessage, loading }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredConversations = conversations.filter((conv) =>
    conv.tenantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (conv.propertyAddress && conv.propertyAddress.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="conversation-list">
      <div className="conversation-list-header">
        <div className="conversation-list-title-row">
          <h2>Messages</h2>
          <button
            className="new-message-btn"
            onClick={onNewMessage}
            title="New message"
          >
            <PenSquare size={18} />
          </button>
        </div>
        <div className="search-container">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="conversations">
        {loading ? (
          <div className="loading-state">
            <Loader2 className="spinner" size={24} />
            <p>Loading conversations...</p>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="empty-conversations">
            <MessageSquare size={32} />
            <p>No conversations yet</p>
          </div>
        ) : (
          filteredConversations.map((conversation) => {
            const hasUnread = conversation.unread > 0;
            return (
              <div
                key={conversation.id}
                className={`conversation-item ${activeConversationId === conversation.id ? 'active' : ''} ${hasUnread ? 'has-unread' : ''}`}
                onClick={() => onSelectConversation(conversation.id)}
              >
                {/* iMessage-style blue dot for unread conversations */}
                {hasUnread && (
                  <div className="unread-dot-indicator">
                    <Circle size={10} fill="#3B82F6" stroke="none" />
                  </div>
                )}
                <div
                  className="avatar"
                  style={{ backgroundColor: getAvatarColor(conversation.tenantName) }}
                >
                  {getInitials(conversation.tenantName)}
                </div>
                <div className="conversation-content">
                  <div className="conversation-header">
                    <span className={`tenant-name ${hasUnread ? 'unread' : ''}`}>{conversation.tenantName}</span>
                    <span className={`timestamp ${hasUnread ? 'unread' : ''}`}>{conversation.timestamp}</span>
                  </div>
                  {conversation.propertyAddress && (
                    <div className="property-address">
                      <Home size={12} />
                      <span>{conversation.propertyAddress}</span>
                    </div>
                  )}
                  <div className="last-message-row">
                    <span className={`last-message ${hasUnread ? 'unread' : ''}`}>{conversation.lastMessage}</span>
                    {hasUnread && (
                      <span className="unread-badge">{conversation.unread}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// Chat Window Component
function ChatWindow({ conversation, messages, onSendMessage, onToggleReadStatus, onCreateServiceRequest, sending, isMobile, onBack }) {
  const [inputValue, setInputValue] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, message: null });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const contextMenuRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu({ visible: false, x: 0, y: 0, message: null });
      }
    };

    if (contextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [contextMenu.visible]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if ((inputValue.trim() || attachment) && !sending) {
      onSendMessage(inputValue.trim(), attachment);
      setInputValue('');
      setAttachment(null);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAttachment(e.target.files[0]);
    }
  };

  const handleContextMenu = (e, message) => {
    e.preventDefault();
    // Only show context menu for inbound messages (received messages)
    if (message.direction === 'inbound') {
      setContextMenu({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        message,
      });
    }
  };

  const handleToggleRead = () => {
    if (contextMenu.message) {
      onToggleReadStatus(contextMenu.message.id, !contextMenu.message.isRead);
      setContextMenu({ visible: false, x: 0, y: 0, message: null });
    }
  };

  if (!conversation) {
    return (
      <div className="chat-window empty">
        <div className="empty-state">
          <MessageSquare size={48} />
          <p>Select a conversation to start messaging</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      <div className="chat-header">
        {isMobile && (
          <button className="chat-back-btn" onClick={onBack} aria-label="Back to conversations">
            <ChevronLeft size={24} />
          </button>
        )}
        <div
          className="avatar"
          style={{ backgroundColor: getAvatarColor(conversation.tenantName) }}
        >
          {getInitials(conversation.tenantName)}
        </div>
        <div className="header-info">
          <h3>{conversation.tenantName}</h3>
          <div className="header-details">
            {conversation.tenantPhone && (
              <span className="detail">
                <Phone size={14} />
                {formatPhoneNumber(conversation.tenantPhone)}
              </span>
            )}
            {conversation.propertyAddress && (
              <span className="detail">
                <Home size={14} />
                {conversation.propertyAddress}
              </span>
            )}
          </div>
        </div>
        <button className="more-btn">
          <MoreVertical size={20} />
        </button>
      </div>

      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message, index) => {
            const previousMessage = index > 0 ? messages[index - 1] : null;
            const showDateSeparator = !previousMessage || isDifferentDay(previousMessage.dateAdded, message.dateAdded);
            
            return (
              <React.Fragment key={message.id}>
                {/* iMessage-style date separator between messages on different days */}
                {showDateSeparator && (
                  <div className="date-separator">
                    <span className="date-separator-text">
                      {formatDateSeparator(message.dateAdded)}
                    </span>
                  </div>
                )}
                <div
                  className={`message ${message.direction === 'outbound' ? 'sent' : 'received'} ${!message.isRead && message.direction === 'inbound' ? 'unread' : ''}`}
                  onContextMenu={(e) => handleContextMenu(e, message)}
                >
                  {/* iMessage-style blue dot for unread received messages */}
                  {!message.isRead && message.direction === 'inbound' && (
                    <div className="message-unread-dot">
                      <Circle size={8} fill="#3B82F6" stroke="none" />
                    </div>
                  )}
                  <div className="message-bubble-row">
                    <div className="message-bubble">
                      {message.content}
                      {message.attachment && (
                        <div className="message-attachment">
                          {message.attachment.type === 'image' ? (
                            <img src={message.attachment.url} alt={message.attachment.name} />
                          ) : (
                            <a href={message.attachment.url} target="_blank" rel="noopener noreferrer">
                              {message.attachment.name}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                    {message.direction === 'inbound' && (
                      <button
                        className="service-request-btn"
                        onClick={() => onCreateServiceRequest(message.content)}
                        aria-label="Create service request"
                      >
                        <Wrench size={14} />
                        <span className="service-request-tooltip">Create service request</span>
                      </button>
                    )}
                  </div>
                  <span className="message-timestamp">
                    {message.direction === 'outbound' && message.senderName
                      ? `${message.senderName} · ${message.timestamp}`
                      : message.timestamp}
                  </span>
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Context Menu for marking messages read/unread */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="message-context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button onClick={handleToggleRead}>
            {contextMenu.message?.isRead ? (
              <>
                <Mail size={16} />
                Mark as Unread
              </>
            ) : (
              <>
                <MailOpen size={16} />
                Mark as Read
              </>
            )}
          </button>
        </div>
      )}

      <form className="message-input-container" onSubmit={handleSubmit}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className="attach-btn"
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip size={20} />
        </button>
        {attachment && (
          <span className="attachment-preview">
            {attachment.name}
            <button type="button" onClick={() => setAttachment(null)}>×</button>
          </span>
        )}
        <input
          type="text"
          placeholder="Type a message..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="message-input"
          disabled={sending}
        />
        <button type="submit" className="send-btn" disabled={sending || (!inputValue.trim() && !attachment)}>
          {sending ? <Loader2 className="spinner" size={20} /> : <Send size={20} />}
        </button>
        <button type="button" className="help-btn">
          <HelpCircle size={20} />
        </button>
      </form>
    </div>
  );
}

// Main Messages Page Component
export default function Messages() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile('(max-width: 767.98px)');
  // On mobile we show one pane at a time: 'list' or 'chat'
  const [mobileView, setMobileView] = useState('list');
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messagesMap, setMessagesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const [showNewThreadModal, setShowNewThreadModal] = useState(false);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');
  const [selectedNewTenant, setSelectedNewTenant] = useState(null);
  const [newThreadMessage, setNewThreadMessage] = useState('');
  const [newThreadLoading, setNewThreadLoading] = useState(false);
  const [newThreadError, setNewThreadError] = useState('');
  const [newThreadSent, setNewThreadSent] = useState(false);

  // Track which message IDs have been marked as read to prevent duplicate API calls
  const markedAsReadRef = useRef(new Set());

  // Tenant thread to auto-select once messages load (set by an SMS deep link)
  const pendingTenantIdRef = useRef(null);

  // On mount, handle an SMS deep-link token (?dl=...) if present, then fetch messages
  useEffect(() => {
    const init = async () => {
      const dlToken = searchParams.get('dl');
      if (dlToken) {
        // Strip the token from the URL so it isn't kept in history
        setSearchParams({}, { replace: true });

        if (hasValidSession()) {
          // Keep the existing session; just open the linked thread
          pendingTenantIdRef.current = decodeDeepLinkTenantId(dlToken);
        } else {
          // Exchange the deep-link token for a new session
          try {
            const response = await fetch(getApiUrl('/api/v1/auth/deeplink'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: dlToken }),
            });

            if (!response.ok) {
              // The deep-link token is invalid or its 24-hour window has
              // expired. Surface a reason so the login page can explain why
              // the user landed there (the token is already stripped, so they
              // can't simply retry the link).
              navigate('/login?reason=link_expired');
              return;
            }

            const data = await response.json();
            localStorage.setItem('access_token', data.access_token);
            localStorage.setItem('user_id', data.user_id);
            localStorage.setItem('first_name', data.first_name);
            if (data.tenant_id != null) {
              pendingTenantIdRef.current = String(data.tenant_id);
            }
          } catch (err) {
            console.error('Deep link login error:', err);
            navigate('/login?reason=link_error');
            return;
          }
        }
      }

      fetchMessages();
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mobile, lock the document to the viewport so the Messages view behaves
  // like a static frame (only the message list scrolls internally).
  useLayoutEffect(() => {
    if (!isMobile) return undefined;
    document.body.classList.add('messages-mobile-lock');
    return () => document.body.classList.remove('messages-mobile-lock');
  }, [isMobile]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Mark messages as read when a conversation is viewed
  // This effect runs when activeConversationId changes and messages are available
  useEffect(() => {
    if (!activeConversationId) return;

    const messages = messagesMap[activeConversationId] || [];
    const unreadMessages = messages.filter(
      (msg) => msg.direction === 'inbound' && !msg.isRead && !markedAsReadRef.current.has(msg.id)
    );

    if (unreadMessages.length === 0) return;

    // Track these messages as being marked to prevent duplicate calls
    const messageIds = unreadMessages.map((msg) => msg.id);
    messageIds.forEach((id) => markedAsReadRef.current.add(id));

    // Mark as read after a small delay so user can see the unread indicators briefly
    const timer = setTimeout(async () => {
      // Mark all unread messages as read via API
      const markPromises = unreadMessages.map((msg) =>
        authFetch(getApiUrl(`/api/v1/message/read/${msg.id}`), {
          method: 'POST',
        }).catch((err) => console.error('Error marking message as read:', err))
      );

      await Promise.all(markPromises);

      // Update local state after API calls complete
      setMessagesMap((prev) => {
        const currentMessages = prev[activeConversationId];
        if (!currentMessages) return prev;

        return {
          ...prev,
          [activeConversationId]: currentMessages.map((msg) =>
            msg.direction === 'inbound' && messageIds.includes(msg.id)
              ? { ...msg, isRead: true }
              : msg
          ),
        };
      });

      // Update conversation unread count
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id !== activeConversationId) return conv;
          const convMessages = messagesMap[conv.id] || [];
          const remainingUnread = convMessages.filter(
            (msg) => msg.direction === 'inbound' && !msg.isRead && !messageIds.includes(msg.id)
          ).length;
          return { ...conv, unread: remainingUnread };
        })
      );
    }, 2200); // 2200ms delay so user sees the unread state briefly

    return () => clearTimeout(timer);
  }, [activeConversationId, messagesMap]);

  // Handle conversation selection
  const handleSelectConversation = useCallback((conversationId) => {
    setActiveConversationId(conversationId);
    // On mobile, opening a conversation switches to the full-screen chat view
    setMobileView('chat');
  }, []);

  // Return to the conversation list on mobile (keeps the selected conversation data)
  const handleBackToList = useCallback(() => {
    setMobileView('list');
  }, []);

  // Toggle read status for a single message
  const handleToggleReadStatus = useCallback(async (messageId, markAsRead) => {
    const endpoint = markAsRead
      ? `/api/v1/message/read/${messageId}`
      : `/api/v1/message/unread/${messageId}`;

    try {
      const response = await authFetch(getApiUrl(endpoint), {
        method: 'POST',
      });

      if (response.ok) {
        // Update the markedAsRead ref based on the action
        if (markAsRead) {
          markedAsReadRef.current.add(messageId);
        } else {
          // Remove from ref so it can be auto-marked as read again when viewed
          markedAsReadRef.current.delete(messageId);
        }

        // Update local state
        setMessagesMap((prev) => {
          const newMap = { ...prev };
          for (const [tenantId, messages] of Object.entries(newMap)) {
            newMap[tenantId] = messages.map((msg) =>
              msg.id === messageId ? { ...msg, isRead: markAsRead } : msg
            );
          }
          return newMap;
        });

        // Recalculate unread counts
        setConversations((prev) =>
          prev.map((conv) => {
            const messages = messagesMap[conv.id] || [];
            const unreadCount = messages.filter(
              (msg) =>
                msg.direction === 'inbound' &&
                (msg.id === messageId ? !markAsRead : !msg.isRead)
            ).length;
            return { ...conv, unread: unreadCount };
          })
        );
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update message status');
      }
    } catch (err) {
      console.error('Error updating read status:', err);
      setError('An error occurred while updating message status');
    }
  }, [messagesMap]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(getApiUrl('/api/v1/message/list'));

      if (response.ok) {
        const data = await response.json();
        processMessages(data.messages || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch messages');
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('An error occurred while fetching messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    setTenantsLoading(true);
    try {
      const response = await authFetch(getApiUrl('/api/v1/tenant/list'));
      if (response.ok) {
        const data = await response.json();
        const withPhone = (data.tenants || []).filter((t) => t.phone);
        setAvailableTenants(withPhone);
      }
    } catch (err) {
      console.error('Error fetching tenants:', err);
    } finally {
      setTenantsLoading(false);
    }
  };

  const resetNewThreadForm = () => {
    setSelectedNewTenant(null);
    setNewThreadMessage('');
    setNewThreadError('');
    setNewThreadLoading(false);
    setNewThreadSent(false);
    setTenantSearchQuery('');
  };

  const handleOpenNewThread = () => {
    resetNewThreadForm();
    setShowNewThreadModal(true);
    fetchTenants();
  };

  const handleNewThreadSend = async () => {
    if (!selectedNewTenant || !newThreadMessage.trim()) return;

    setNewThreadLoading(true);
    setNewThreadError('');

    try {
      const response = await authFetch(getApiUrl('/api/v1/message/send'), {
        method: 'POST',
        body: JSON.stringify({
          tenant_id: selectedNewTenant.id,
          message_body: newThreadMessage.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setNewThreadError(data.error || 'Failed to send message.');
        setNewThreadLoading(false);
        return;
      }

      setNewThreadSent(true);

      const newMessage = data.message;
      const tenantIdStr = String(selectedNewTenant.id);
      const tenantName = `${selectedNewTenant.first_name || ''} ${selectedNewTenant.last_name || ''}`.trim() || 'Unknown';

      setMessagesMap((prev) => ({
        ...prev,
        [tenantIdStr]: [
          ...(prev[tenantIdStr] || []),
          {
            id: String(newMessage.id),
            content: newMessage.message_body,
            timestamp: formatMessageTime(newMessage.date_added),
            dateAdded: newMessage.date_added,
            direction: 'outbound',
            isRead: true,
            senderName: newMessage.sender_name || null,
          },
        ],
      }));

      setConversations((prev) => {
        const existing = prev.find((c) => c.id === tenantIdStr);
        if (existing) {
          return prev.map((c) =>
            c.id === tenantIdStr
              ? { ...c, lastMessage: newThreadMessage.trim(), timestamp: 'Just now' }
              : c
          );
        }
        return [
          {
            id: tenantIdStr,
            tenantId: selectedNewTenant.id,
            tenantName,
            tenantPhone: selectedNewTenant.phone,
            propertyId: newMessage.property_id || null,
            propertyAddress: newMessage.property_address || null,
            lastMessage: newThreadMessage.trim(),
            timestamp: 'Just now',
            unread: 0,
          },
          ...prev,
        ];
      });

      setActiveConversationId(tenantIdStr);

      setTimeout(() => {
        setShowNewThreadModal(false);
        resetNewThreadForm();
      }, 1200);
    } catch (err) {
      console.error('New thread send error:', err);
      setNewThreadError('An error occurred. Please try again.');
      setNewThreadLoading(false);
    }
  };

  // Process raw messages into conversations and messages map
  const processMessages = (rawMessages) => {
    // Group messages by tenant
    const messagesByTenant = {};

    rawMessages.forEach((msg) => {
      const tenantId = msg.tenant_id;
      if (!messagesByTenant[tenantId]) {
        messagesByTenant[tenantId] = {
          tenantId,
          tenantName: msg.tenant_name || 'Unknown',
          tenantPhone: msg.tenant_phone,
          propertyId: msg.property_id || null,
          propertyAddress: msg.property_address,
          messages: [],
        };
      }
      messagesByTenant[tenantId].messages.push(msg);
    });

    // Sort messages within each conversation by date
    Object.values(messagesByTenant).forEach((conv) => {
      conv.messages.sort((a, b) => new Date(a.date_added) - new Date(b.date_added));
    });

    // Create messages map (keyed by tenantId as string) with read status
    const newMessagesMap = {};
    Object.entries(messagesByTenant).forEach(([tenantId, conv]) => {
      newMessagesMap[tenantId] = conv.messages.map((msg) => ({
        id: String(msg.id),
        content: msg.message_body,
        timestamp: formatMessageTime(msg.date_added),
        dateAdded: msg.date_added,
        direction: msg.message_to === msg.tenant_phone ? 'outbound' : 'inbound',
        isRead: msg.message_to === msg.tenant_phone ? true : (msg.message_read ?? true),
        senderName: msg.sender_name || null,
      }));
    });

    // Create conversations array with unread counts
    const conversationsArr = Object.values(messagesByTenant).map((conv) => {
      const lastMsg = conv.messages[conv.messages.length - 1];
      const tenantIdStr = String(conv.tenantId);
      const convMessages = newMessagesMap[tenantIdStr] || [];
      // Count unread inbound messages
      const unreadCount = convMessages.filter(
        (msg) => msg.direction === 'inbound' && !msg.isRead
      ).length;

      return {
        id: tenantIdStr,
        tenantId: conv.tenantId,
        tenantName: conv.tenantName,
        tenantPhone: conv.tenantPhone,
        propertyId: conv.propertyId,
        propertyAddress: conv.propertyAddress,
        lastMessage: lastMsg ? lastMsg.message_body : '',
        timestamp: lastMsg ? formatTimestamp(lastMsg.date_added) : '',
        unread: unreadCount,
      };
    });

    // Sort conversations by last message time (most recent first)
    conversationsArr.sort((a, b) => {
      const aTime = messagesByTenant[a.tenantId]?.messages.slice(-1)[0]?.date_added || '';
      const bTime = messagesByTenant[b.tenantId]?.messages.slice(-1)[0]?.date_added || '';
      return new Date(bTime) - new Date(aTime);
    });

    setConversations(conversationsArr);
    setMessagesMap(newMessagesMap);

    // A deep link targets a specific tenant thread - open it directly
    const pendingTenantId = pendingTenantIdRef.current;
    if (pendingTenantId && newMessagesMap[pendingTenantId]) {
      pendingTenantIdRef.current = null;
      setActiveConversationId(pendingTenantId);
      // On mobile, land directly in the chat view for the thread
      setMobileView('chat');
      return;
    }

    // Select first conversation if none selected
    if (!activeConversationId && conversationsArr.length > 0) {
      setActiveConversationId(conversationsArr[0].id);
    }
  };

  const activeConversation = useMemo(() =>
    conversations.find((c) => c.id === activeConversationId) || null,
    [conversations, activeConversationId]
  );

  const activeMessages = useMemo(() =>
    activeConversationId ? messagesMap[activeConversationId] || [] : [],
    [messagesMap, activeConversationId]
  );

  const handleCreateServiceRequest = useCallback((messageText) => {
    if (!activeConversation) return;
    navigate('/service', {
      state: {
        prefill: true,
        tenantId: activeConversation.tenantId,
        tenantDescription: messageText,
        propertyId: activeConversation.propertyId,
        propertyAddress: activeConversation.propertyAddress,
      },
    });
  }, [activeConversation, navigate]);

  const handleSendMessage = async (content, attachment) => {
    if (!activeConversation) return;

    // For now, we only support text messages (no attachments via API)
    if (!content && attachment) {
      setError('File attachments are not yet supported');
      return;
    }

    try {
      setSending(true);
      setError(null);

      const response = await authFetch(getApiUrl('/api/v1/message/send'), {
        method: 'POST',
        body: JSON.stringify({
          tenant_id: activeConversation.tenantId,
          message_body: content,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const newMessage = data.message;

        // Add new message to the messages map
        setMessagesMap((prev) => ({
          ...prev,
          [activeConversationId]: [
            ...(prev[activeConversationId] || []),
            {
              id: String(newMessage.id),
              content: newMessage.message_body,
              timestamp: formatMessageTime(newMessage.date_added),
              direction: 'outbound',
              senderName: newMessage.sender_name || null,
            },
          ],
        }));

        // Update last message in conversation list
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === activeConversationId
              ? {
                  ...conv,
                  lastMessage: content,
                  timestamp: 'Just now',
                }
              : conv
          )
        );
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('An error occurred while sending the message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout pageTitle="Messages">
      {error && (
        <div className="error-banner">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      <div className="messages-content">
        {/* Conversation List - hidden on mobile when viewing a chat */}
        {(!isMobile || mobileView === 'list') && (
          <ConversationList
            conversations={conversations}
            activeConversationId={activeConversationId}
            onSelectConversation={handleSelectConversation}
            onNewMessage={handleOpenNewThread}
            loading={loading}
          />
        )}

        {/* Chat Window - hidden on mobile when viewing the list */}
        {(!isMobile || mobileView === 'chat') && (
          <ChatWindow
            conversation={activeConversation}
            messages={activeMessages}
            onSendMessage={handleSendMessage}
            onToggleReadStatus={handleToggleReadStatus}
            onCreateServiceRequest={handleCreateServiceRequest}
            sending={sending}
            isMobile={isMobile}
            onBack={handleBackToList}
          />
        )}
      </div>

      {/* New Message Thread Modal */}
      <Dialog
        open={showNewThreadModal}
        onOpenChange={(open) => {
          setShowNewThreadModal(open);
          if (!open) resetNewThreadForm();
        }}
      >
        <DialogContent className="max-w-md new-message-modal">
          <DialogHeader>
            <DialogTitle className="modal-title">
              <PenSquare size={22} className="modal-title-icon" />
              New Message
            </DialogTitle>
            <DialogDescription>
              Select a tenant and compose your message.
            </DialogDescription>
          </DialogHeader>

          {newThreadSent ? (
            <div className="modal-success">
              <div className="modal-success-icon">
                <Send size={28} className="modal-title-icon" />
              </div>
              <h4 className="modal-success-title">Message Sent!</h4>
              <p className="modal-success-text">
                The conversation is now open.
              </p>
            </div>
          ) : (
            <div className="modal-body">
              {!selectedNewTenant ? (
                <div>
                  <label className="modal-field-label">
                    Select Tenant
                  </label>
                  <div className="modal-search">
                    <Search size={16} className="modal-search-icon" />
                    <input
                      type="text"
                      placeholder="Search tenants..."
                      value={tenantSearchQuery}
                      onChange={(e) => setTenantSearchQuery(e.target.value)}
                      className="modal-search-input"
                    />
                  </div>
                  <div className="modal-tenant-list">
                    {tenantsLoading ? (
                      <div className="modal-tenant-loading">
                        <Loader2 size={18} className="spinner" />
                        <span>Loading tenants...</span>
                      </div>
                    ) : availableTenants.filter((t) => {
                        const name = `${t.first_name || ''} ${t.last_name || ''}`.trim().toLowerCase();
                        const q = tenantSearchQuery.toLowerCase();
                        return name.includes(q) || (t.email || '').toLowerCase().includes(q) || (t.phone || '').includes(q);
                      }).length === 0 ? (
                      <div className="modal-tenant-empty">
                        No tenants with phone numbers found
                      </div>
                    ) : (
                      availableTenants
                        .filter((t) => {
                          const name = `${t.first_name || ''} ${t.last_name || ''}`.trim().toLowerCase();
                          const q = tenantSearchQuery.toLowerCase();
                          return name.includes(q) || (t.email || '').toLowerCase().includes(q) || (t.phone || '').includes(q);
                        })
                        .map((t) => {
                          const name = `${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email;
                          const initials = t.first_name && t.last_name
                            ? `${t.first_name[0]}${t.last_name[0]}`.toUpperCase()
                            : '??';
                          return (
                            <div
                              key={t.id}
                              onClick={() => setSelectedNewTenant(t)}
                              className="modal-tenant-row"
                            >
                              <div
                                className="modal-avatar"
                                style={{ backgroundColor: getAvatarColor(name) }}
                              >
                                {initials}
                              </div>
                              <div className="modal-contact-info">
                                <div className="modal-contact-name">{name}</div>
                                <div className="modal-contact-phone">
                                  <Phone size={11} />
                                  {formatPhoneNumber(t.phone)}
                                </div>
                              </div>
                            </div>
                          );
                        })
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="modal-field-label">
                      To
                    </label>
                    <div className="modal-selected-tenant">
                      <div
                        className="modal-avatar"
                        style={{
                          backgroundColor: getAvatarColor(
                            `${selectedNewTenant.first_name || ''} ${selectedNewTenant.last_name || ''}`.trim()
                          ),
                        }}
                      >
                        {selectedNewTenant.first_name && selectedNewTenant.last_name
                          ? `${selectedNewTenant.first_name[0]}${selectedNewTenant.last_name[0]}`.toUpperCase()
                          : '??'}
                      </div>
                      <div className="modal-contact-info">
                        <div className="modal-contact-name">
                          {`${selectedNewTenant.first_name || ''} ${selectedNewTenant.last_name || ''}`.trim() || selectedNewTenant.email}
                        </div>
                        <div className="modal-contact-phone">
                          <Phone size={11} />
                          {formatPhoneNumber(selectedNewTenant.phone)}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedNewTenant(null)}
                        className="modal-change-btn"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="modal-field-label">
                      Message <span className="modal-required">*</span>
                    </label>
                    <textarea
                      placeholder="Type your message..."
                      value={newThreadMessage}
                      onChange={(e) => setNewThreadMessage(e.target.value)}
                      rows={4}
                      className="modal-textarea"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {!newThreadSent && selectedNewTenant && (
            <>
              {newThreadError && (
                <p className="modal-error">{newThreadError}</p>
              )}
              <DialogFooter className="modal-footer">
                <button
                  onClick={() => {
                    setShowNewThreadModal(false);
                    resetNewThreadForm();
                  }}
                  className="modal-btn modal-btn-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNewThreadSend}
                  disabled={!newThreadMessage.trim() || newThreadLoading}
                  className="modal-btn modal-btn-send"
                >
                  {newThreadLoading ? (
                    <Loader2 size={18} className="spinner" />
                  ) : (
                    <Send size={18} />
                  )}
                  {newThreadLoading ? 'Sending...' : 'Send Message'}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
